// src/components/Testimonials/TestimonialSection.jsx

import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, Skeleton, Fade } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { fetchTestimonials } from "../../redux/actions/testimonials";

export default function TestimonialSection() {
  const dispatch = useDispatch();

  const { testimonials, loading, error } = useSelector(
    (state) => state.testimonialsState,
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animate, setAnimate] = useState(true);

  useEffect(() => {
    dispatch(fetchTestimonials());
  }, [dispatch]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [testimonials]);

  const triggerTransition = (newIndex) => {
    setAnimate(false);
    setTimeout(() => {
      setCurrentIndex(newIndex);
      setAnimate(true);
    }, 300);
  };

  const prev = () => {
    const newIndex =
      currentIndex === 0 ? reversedTestimonials.length - 1 : currentIndex - 1;
    triggerTransition(newIndex);
  };

  const next = () => {
    const newIndex =
      currentIndex === reversedTestimonials.length - 1 ? 0 : currentIndex + 1;
    triggerTransition(newIndex);
  };

  if (loading) {
    return (
      <Box sx={{ py: 10, width: "100%" }}>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 10, textAlign: "center", width: "100%" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!testimonials || !testimonials.length) return null;
  const reversedTestimonials = [...testimonials].reverse();
  const { quote, person_name, image_url } = reversedTestimonials[currentIndex];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, md: 8 },
        backgroundColor: "#ffffff",
        width: "100%", // Ensures the outer section is full width
      }}
    >
      <Box sx={{ width: "100%" }}>
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 300,
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              mb: 2,
              color: "black",
            }}
          >
            TESTIMONIALS
          </Typography>
          <Box
            sx={{
              width: "40px",
              height: "1px",
              backgroundColor: "black",
              margin: "0 auto",
            }}
          />
        </Box>

        {/* Clean Side-by-Side Layout */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "stretch",
            minHeight: { md: "500px" },
            backgroundColor: "#212121",
            p: { xs: 4, md: 6, lg: 10 },
          }}
        >
          {/* Image Column */}
          <Box>
            <Fade in={animate} timeout={400}>
              <img
                key={currentIndex}
                src={image_url}
                alt={person_name}
                style={{
                  width: "100%",
                  height: "100%",
                  maxHeight: "500px",
                  objectFit: "contain",
                }}
              />
            </Fade>
          </Box>

          {/* Text Column */}
          <Box
            sx={{
              width: { xs: "100%", md: "70%" },
              px: { xs: 4, md: 6, lg: 10 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Fade in={animate} timeout={400}>
              <Box>
                <Typography
                  sx={{
                    fontSize: "3rem",
                    color: "#e0e0e0",
                    lineHeight: 0.5,
                    mb: 4,
                    fontFamily: "serif",
                  }}
                ></Typography>

                <Typography
                  component="blockquote"
                  sx={{
                    fontSize: { xs: "0.95rem", md: "1.05rem" },
                    lineHeight: 1.8,
                    fontWeight: 300,
                    mb: 4,
                    color: "#ffff",
                    fontStyle: "italic",
                  }}
                >
                  {quote}
                </Typography>

                {/* Name & Navigation Row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between", // Pushes name left, arrows right
                    flexWrap: "wrap",
                    gap: 3,
                    mt: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "#ffff",
                      lineHeight: 1.5, // Added for multi-line readability
                    }}
                  >
                    {/* Replaced {person_name} with the mapped output */}
                    {person_name
                      ?.split(/(?<=[|])/)
                      .map((part, index, array) => (
                        <React.Fragment key={index}>
                          {part.trim()}
                          {index < array.length - 1 && <br />}
                        </React.Fragment>
                      ))}
                  </Typography>

                  {/* Navigation Buttons inline with name */}
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <IconButton
                      onClick={prev}
                      aria-label="Previous testimonial"
                      sx={{
                        border: "1px solid #ddd",
                        borderRadius: "50%",
                        width: 44,
                        height: 44,
                        color: "#ffff",
                        "&:hover": {
                          borderColor: "black",
                          backgroundColor: "#ffff",
                          color: "black",
                        },
                      }}
                    >
                      <KeyboardArrowLeft />
                    </IconButton>
                    <IconButton
                      onClick={next}
                      aria-label="Next testimonial"
                      sx={{
                        border: "1px solid #ddd",
                        borderRadius: "50%",
                        width: 44,
                        height: 44,
                        color: "#ffff",
                        "&:hover": {
                          borderColor: "black",
                          backgroundColor: "#ffff",
                          color: "black",
                        },
                      }}
                    >
                      <KeyboardArrowRight />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
